import Axios, {AxiosInstance, AxiosRequestConfig} from 'axios';
import {injectable} from 'inversify-props';

import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import {IApiService} from '@/services/IApi.service';
import Jwt from '@/model/Jwt';
import {Time} from '@/utils/Time';

export type ErrorHandler = (error: Error) => Promise<void>;

@injectable()
export class ApiService implements IApiService {

  public unauthorizedErrorHandler: ErrorHandler | null = null;

  public notAvailableErrorHandler: ErrorHandler | null = null;

  public Timeout: number = 5000;

  private logger: ILogger = new LogdownLogger('ApiService');

  private httpService: AxiosInstance = Axios.create({
    timeout: this.Timeout,
  });

  public configure(remote: string, secured: boolean): void {
    this.logger.trace(`Setting remote '${remote}' (${secured ? 'SSL' : 'no SSL'})`);
    this.setUrl(`${secured ? 'https' : 'http'}://${remote}${process.env.VUE_APP_API_ENDPOINT}`);
  }

  public setToken(token: Jwt): void {
    this.logger.trace(`Setting header token`);
    this.httpService.defaults.headers.common.Authorization = `Bearer ${token.string}`;
  }

  public removeToken(): void {
    this.logger.trace(`Removing auth header`);
    this.httpService.defaults.headers.common = {};
  }

  public async check(timeoutMs: number = 5000): Promise<boolean> {
    this.logger.trace(`Checking API availability`, this.httpService.defaults.baseURL, timeoutMs);

    try {
      await this.httpService.get('', {
        timeout: timeoutMs,
      });
      this.logger.trace(`API is available`);
      return true;
    } catch (error) {
      this.logger.warn(`API is NOT available`, error);
      return false;
    }
  }

  public async waitConnected(timeout: Date, checkInterval: number = 1000): Promise<void> {

    if (timeout < new Date()) {
      throw new Error(`Timed out before beginning`);
    }

    this.logger.trace('Waiting to be connected to the device until', timeout);

    let connected = false;
    /* If reconnected */
    while (!connected) {

      await Time.sleep(checkInterval);

      if (new Date() > timeout) {
        throw new Error(`Timed out`);
      }

      try {
        connected = await this.check((timeout.getTime() - new Date().getTime()));
      } catch (error) {
        this.logger.error('Could not reconnect', error);
      }
    }

    return;
  }

  public async get(url: string, config?: AxiosRequestConfig): Promise<any> {
    this.logger.trace(`API GET`, url, config !== undefined ? config : '');
    return await this.executeRequest(async () => await this.httpService.get(url, config));
  }

  public async post(url: string, data: any, config?: AxiosRequestConfig): Promise<any> {
    this.logger.trace(`API POST`, url, data, config !== undefined ? config : '');
    return await this.executeRequest(() => this.httpService.post(url, data, config));
  }

  public async put(url: string, data: any, config?: AxiosRequestConfig): Promise<any> {
    this.logger.trace(`API PUT`, url, data, config !== undefined ? config : '');
    return await this.executeRequest(() => this.httpService.put(url, data, config));
  }

  public async delete(url: string, config?: AxiosRequestConfig): Promise<any> {
    this.logger.trace(`API DELETE`, url, config !== undefined ? config : '');
    return await this.executeRequest(() => this.httpService.delete(url, config));
  }

  private async executeRequest(request: () => Promise<any>): Promise<any> {
    try {
      const response = await request();

      this.logger.trace(`Request executed`, response);

      return response.data;
    } catch (error) {
      if (!error.response) {
        this.logger.error(`An error occurred during execution of a request to the API`, error);
        if (this.notAvailableErrorHandler) {
          this.notAvailableErrorHandler(error);
        }
        throw error;
      }

      if (error.response.status === 401) {
        if (this.unauthorizedErrorHandler) {
          await this.unauthorizedErrorHandler(error);
        }

        return;
      }

      this.logger.error(`An error occurred during execution of a request to the API`, error);
      throw error.response.data;
    }
  }

  private setUrl(baseURL: string): void {
    this.logger.trace(`Setting base URL`, baseURL);
    this.httpService.defaults.baseURL = baseURL;
  }
}
